import { cn } from "@universe/ui/lib/utils";
import { format } from "date-fns";
import { usePathname } from "next/navigation";
import React, { useMemo, useState } from "react";

import WelcomeEmptyState from "@/components/dashboard/welcome-empty-state";
import LedgerDateRangeEmptyState from "@/components/ledger/ledger-date-range-empty-state";
import LedgerListTransition from "@/components/ledger/ledger-list-transition";
import { LEDGER_ENTRY_GROUP_HEADER_DATE_FORMAT } from "@/constants/dateTime";
import { Route } from "@/enums/route";
import { useListItemReveal } from "@/hooks/useListItemReveal";
import { useDashboardHeader, useLedgerEntryList } from "@/providers";
import groupByField, {
  combineFxTicketPairs,
  compareLedgerEntries,
} from "@/utils/groupByField";

import LedgerEntryDrawerDetails from "./ledger-entry-drawer-details";
import LedgerEntryGroup from "./ledger-entry-group";
import LedgerEntryHeader from "./ledger-entry-header";
import LedgerEntryItem from "./ledger-entry-item";
import LedgerEntryItemCombined from "./ledger-entry-item-combined";

interface LedgerEntryListProps {
  className?: string;
}

export default function LedgerEntryList({ className }: LedgerEntryListProps) {
  const {
    data: ledgerEntries,
    isInitialLoad,
    isLoading,
    isFetching,
    isEmpty,
    isPaginating,
    loadMoreRef,
    filters,
    product,
  } = useLedgerEntryList();
  const { activityDateGroupTop } = useDashboardHeader();

  const pathname = usePathname();
  const isDashboardPage = pathname === Route.Dashboard;

  const isInProgress = isLoading || isFetching;
  // Only page 2+ — never flash a bottom skeleton on cached page-1 refetch
  // (e.g. clearing the date filter back onto the warm unfiltered cache).
  const showFetchSkeleton = isPaginating;

  const hasDateFilters = Boolean(filters?.startDate || filters?.endDate);
  const listIdentity = useMemo(
    () =>
      JSON.stringify({
        startDate: filters?.startDate ?? null,
        endDate: filters?.endDate ?? null,
        product: product ?? null,
      }),
    [filters?.startDate, filters?.endDate, product]
  );

  const [selectedEntry, setSelectedEntry] =
    useState<LedgerEntryOrCombined | null>(null);

  const [selectedIndex, setSelectedIndex] = useState<string | null>(null);

  const processedEntries = useMemo(() => {
    if (isDashboardPage) {
      return combineFxTicketPairs(ledgerEntries as LedgerEntryResolvedType[]);
    }
    return ledgerEntries as LedgerEntryResolvedType[];
  }, [ledgerEntries, isDashboardPage]);

  const groupedLedgerEntries = useMemo(
    () =>
      groupByField(processedEntries as LedgerEntryOrCombined[], "valueDate", {
        sortBeforeGrouping: compareLedgerEntries,
        sortItems: compareLedgerEntries,
      }),
    [processedEntries]
  );

  const itemKeys = useMemo(
    () => processedEntries.map((entry) => entry.uuid),
    [processedEntries]
  );
  const revealingItemDelays = useListItemReveal(
    itemKeys,
    isInitialLoad,
    isEmpty,
    { listIdentity }
  );

  const handleOnClose = () => {
    setSelectedEntry(null);
    setSelectedIndex(null);
  };

  return (
    <div className={cn("flex relative", className)}>
      <div className="w-full relative">
        <div
          id="ledger-entry-list"
          className={cn(
            "absolute top-[-110px] h-0 left-0 right-0 w-full",
            !isDashboardPage && "top-[-150px]"
          )}
        />
        <LedgerEntryHeader />
        <div className="mx-[-12px] relative">
          <LedgerListTransition
            isInitialLoad={isInitialLoad}
            isEmpty={isEmpty}
            emptyMessage={
              hasDateFilters ? (
                <LedgerDateRangeEmptyState />
              ) : (
                <WelcomeEmptyState currency={product} />
              )
            }
            skeletonCount={12}
            minHeight="240px"
            showFetchSkeleton={showFetchSkeleton}
          >
            <LedgerEntryGroup<LedgerEntryOrCombined>
              groupedList={groupedLedgerEntries}
              dateGroupTop={activityDateGroupTop}
              revealingItemDelays={revealingItemDelays}
              renderGroupTitle={(date) =>
                format(new Date(date), LEDGER_ENTRY_GROUP_HEADER_DATE_FORMAT)
              }
              renderGroupItem={(item, index) => {
                if ("isCombined" in item && item.isCombined) {
                  return (
                    <LedgerEntryItemCombined
                      index={index}
                      selectedIndex={selectedIndex}
                      entry={item}
                      setSelectedEntry={() => {
                        setSelectedIndex(`${item.uuid}-${index}`);
                        setSelectedEntry(item);
                      }}
                    />
                  );
                }
                const regularEntry = item as LedgerEntryResolvedType;
                return (
                  <LedgerEntryItem
                    index={index}
                    selectedIndex={selectedIndex}
                    entry={regularEntry}
                    setSelectedEntry={() => {
                      setSelectedIndex(`${regularEntry.uuid}-${index}`);
                      setSelectedEntry(regularEntry);
                    }}
                  />
                );
              }}
            />
          </LedgerListTransition>
          {!isInitialLoad && !isInProgress && (
            <div ref={loadMoreRef} className="h-6" />
          )}
        </div>
      </div>

      <LedgerEntryDrawerDetails entry={selectedEntry} onClose={handleOnClose} />
    </div>
  );
}
